import { ref } from 'vue';
import axios from 'axios';

/**
 * Composable for handling profile picture uploads during tutor onboarding
 * Uploads images directly to Cloudflare R2 (public bucket) using presigned URLs
 *
 * @param {string} slug - The tutor application slug
 * @param {string} presignedUrlEndpoint - Endpoint to get presigned upload URL
 * @returns {Object} Upload utilities and state
 */
export function useProfilePictureUpload(slug, presignedUrlEndpoint) {
    const isUploading = ref(false);
    const uploadProgress = ref(0);
    const uploadError = ref(null);

    /**
     * Upload a profile picture directly to Cloudflare R2 (client-side)
     *
     * @param {Blob} imageBlob - The cropped image blob to upload
     * @param {string} filename - Original filename
     * @param {Object} options - Additional options (onSuccess, onError)
     * @returns {Promise<Object>} Upload result with path and public URL
     */
    const uploadProfilePicture = async (imageBlob, filename, options = {}) => {
        isUploading.value = true;
        uploadProgress.value = 0;
        uploadError.value = null;

        try {
            // Step 1: Request a presigned upload URL from the backend
            const signedUrlResponse = await axios.post(presignedUrlEndpoint, {
                filename: filename,
                type: 'photo',
                content_type: imageBlob.type || 'image/jpeg',
            }, {
                headers: {
                    'X-Tutor-Slug': slug,
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                }
            });

            if (!signedUrlResponse.data.success) {
                throw new Error(signedUrlResponse.data.message || 'Failed to generate signed URL');
            }

            const { upload_url, path, filename: storedFilename, public_url } = signedUrlResponse.data.data;

            // Step 2: Upload directly to R2 using the presigned URL
            await axios.put(upload_url, imageBlob, {
                headers: {
                    'Content-Type': imageBlob.type || 'image/jpeg',
                },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    }
                },
                transformRequest: [(data) => data],
                maxRedirects: 0,
                withCredentials: false,
            });

            // Step 3: Return upload result
            const result = {
                path,
                filename: storedFilename,
                original_name: filename,
                public_url,
                size: imageBlob.size,
                mime_type: imageBlob.type || 'image/jpeg',
            };

            uploadProgress.value = 100;

            if (options.onSuccess) {
                options.onSuccess(result);
            }

            return result;

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
            uploadError.value = errorMessage;

            if (options.onError) {
                options.onError(errorMessage);
            }

            throw new Error(errorMessage);

        } finally {
            isUploading.value = false;
        }
    };

    return {
        // State
        isUploading,
        uploadProgress,
        uploadError,

        // Methods
        uploadProfilePicture,
    };
}
